import React, { useEffect, useMemo, useState } from 'react';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { apiService } from '../services/api';
import { useEscapeToClose } from '../hooks/useEscapeToClose';
import '../components/Auth/RegisterModal.css';
import './ManageCompetitionModals.css';

type AccessType = 'viewer' | 'competitor' | 'judge' | 'host';

type AccessFilter = 'all' | AccessType;
type SortOption = 'username_asc' | 'username_desc' | 'score_desc' | 'score_asc' | 'latest_submission_desc';

interface AccessEntry {
  username: string;
  access_type: AccessType;
  email?: string;
  first_name?: string;
  last_name?: string;
  team_id?: number;
  team_name?: string;
  is_team_lead?: boolean;
  host_privilege?: string;
  current_votes?: number;
  max_votes?: number;
  has_submission?: boolean;
  latest_version?: number;
  score?: number;
  final_ranking?: number;
}

interface AccessListResponse {
  competition_id: number;
  visibility?: string;
  entries: AccessEntry[];
}

interface ManageCompetitionAccessListModalProps {
  isOpen: boolean;
  onClose: () => void;
  competitionId: number;
  canViewAllSubmissions?: boolean;
  canViewScores?: boolean;
}

const ManageCompetitionAccessListModal: React.FC<ManageCompetitionAccessListModalProps> = ({
  isOpen,
  onClose,
  competitionId,
  canViewAllSubmissions = false,
  canViewScores = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [accessFilter, setAccessFilter] = useState<AccessFilter>('all');
  const [sortBy, setSortBy] = useState<SortOption>(canViewScores ? 'score_desc' : 'username_asc');
  const [data, setData] = useState<AccessListResponse | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setQuery('');
    setAccessFilter('all');
    setSortBy(canViewScores ? 'score_desc' : 'username_asc');
    setError('');
    setData(null);

    const run = async () => {
      setLoading(true);
      try {
        const res = await apiService.get(`/competitions/${competitionId}/access`);
        setData(res.data);
      } catch (err: any) {
        console.error('Failed to load competition access list', err);
        const msg = err?.response?.data?.detail || err?.message || 'Failed to load access list';
        setError(String(msg));
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [competitionId, isOpen, canViewScores]);

  const filtered = useMemo(() => {
    const entries = data?.entries || [];
    const q = query.trim().toLowerCase();
    return entries.filter((e) => {
      if (accessFilter !== 'all' && e.access_type !== accessFilter) return false;
      if (!q) return true;
      const hay = `${e.username} ${e.email || ''} ${e.first_name || ''} ${e.last_name || ''} ${e.team_name || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [accessFilter, data?.entries, query]);

  const sorted = useMemo(() => {
    const rows = [...filtered];
    const scoreValue = (entry: AccessEntry): number | null => (
      typeof entry.score === 'number' && !Number.isNaN(entry.score) ? entry.score : null
    );

    rows.sort((a, b) => {
      if (sortBy === 'username_asc') return a.username.localeCompare(b.username);
      if (sortBy === 'username_desc') return b.username.localeCompare(a.username);

      if (sortBy === 'latest_submission_desc') {
        const av = a.latest_version ?? -1;
        const bv = b.latest_version ?? -1;
        if (bv !== av) return bv - av;
        return a.username.localeCompare(b.username);
      }

      const av = scoreValue(a);
      const bv = scoreValue(b);
      if (av === null && bv === null) return a.username.localeCompare(b.username);
      if (av === null) return 1;
      if (bv === null) return -1;
      if (sortBy === 'score_desc') {
        if (bv !== av) return bv - av;
        return a.username.localeCompare(b.username);
      }
      if (av !== bv) return av - bv;
      return a.username.localeCompare(b.username);
    });

    return rows;
  }, [filtered, sortBy]);

  const canOpenSubmission = (entry: AccessEntry): boolean =>
    !!canViewAllSubmissions && entry.access_type === 'competitor' && !!entry.has_submission;

  const formatScore = (entry: AccessEntry): string => {
    if (typeof entry.score !== 'number' || Number.isNaN(entry.score)) return '-';
    if (entry.final_ranking && entry.final_ranking > 0) {
      return `${entry.score.toFixed(2)} (rank ${Math.round(entry.final_ranking)})`;
    }
    return entry.score.toFixed(2);
  };

  useEscapeToClose(onClose, { enabled: isOpen });

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div className="register-modal-backdrop" onClick={handleBackdropClick}>
      <div
        className="register-modal"
        role="dialog"
        aria-modal="true"
        style={{ width: 'min(1100px, 96vw)', maxWidth: '1100px' }}
      >
        <div className="register-modal__header">
          <h2 className="register-modal__title">Competition Access & Submissions</h2>
          <button type="button" className="register-modal__close" onClick={onClose} aria-label="Close modal">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="register-modal__form">
          {error && (
            <div className="register-modal__error" role="alert">
              {error}
            </div>
          )}

          {loading && <LoadingSpinner text="Loading access list..." />}

          {!loading && data && (
            <>
              <div className="register-modal__fields">
                <label className="input-wrapper">
                  <span className="input__label">Access filter</span>
                  <select
                    className="input input--default input--md"
                    value={accessFilter}
                    onChange={(e) => setAccessFilter(e.target.value as AccessFilter)}
                  >
                    <option value="all">all</option>
                    <option value="host">host</option>
                    <option value="competitor">competitor</option>
                    <option value="judge">judge</option>
                    <option value="viewer">viewer</option>
                  </select>
                </label>

                <Input
                  name="query"
                  label="Search"
                  placeholder="Filter by username, team, or email"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
                <label className="input-wrapper">
                  <span className="input__label">Sort by</span>
                  <select
                    className="input input--default input--md"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as SortOption)}
                  >
                    <option value="username_asc">username (A-Z)</option>
                    <option value="username_desc">username (Z-A)</option>
                    <option value="latest_submission_desc">latest submission version</option>
                    {canViewScores && <option value="score_desc">score (high to low)</option>}
                    {canViewScores && <option value="score_asc">score (low to high)</option>}
                  </select>
                </label>

                <div className="mcm-text-secondary" style={{ fontSize: '0.9rem' }}>
                  Showing <strong>{filtered.length}</strong> entries
                  {data.visibility ? (
                    <>
                      {' '}• Visibility: <strong>{data.visibility}</strong>
                    </>
                  ) : null}
                </div>

                <div className="mcm-table-wrap" style={{ maxHeight: 380, overflow: 'auto', borderRadius: 10 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr className="mcm-table-head" style={{ textAlign: 'left' }}>
                        <th style={{ padding: '0.65rem' }}>User</th>
                        <th style={{ padding: '0.65rem' }}>Team</th>
                        <th style={{ padding: '0.65rem' }}>Access</th>
                        <th style={{ padding: '0.65rem' }}>Status</th>
                        <th style={{ padding: '0.65rem' }}>Score</th>
                        <th style={{ padding: '0.65rem' }}>Submission</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sorted.map((e) => {
                        const judgeVotes = e.access_type === 'judge' ? `${e.current_votes ?? 0}/${e.max_votes ?? 0}` : null;
                        const voteDone = e.access_type === 'judge' && (e.max_votes ?? 0) > 0 && (e.current_votes ?? 0) >= (e.max_votes ?? 0);
                        const hostStatus = e.access_type === 'host' ? `host ${e.host_privilege || ''}`.trim() : null;
                        const submissionStatus = e.access_type === 'competitor'
                          ? (e.has_submission ? `submission v${e.latest_version}` : 'no submission')
                          : null;

                        const statusText = judgeVotes
                          ? `evaluations ${judgeVotes}`
                          : hostStatus
                            ? hostStatus
                          : submissionStatus
                            ? submissionStatus
                            : '-';

                        return (
                          <tr key={`${e.access_type}:${e.username}`} className="mcm-row">
                            <td style={{ padding: '0.65rem' }}>
                              <div style={{ fontWeight: 700 }}>{e.username}</div>
                              <div className="mcm-text-secondary" style={{ fontSize: '0.85rem' }}>{e.email || ''}</div>
                            </td>
                            <td style={{ padding: '0.65rem' }}>
                              {e.team_name ? (
                                <div>
                                  <div>{e.team_name}</div>
                                  {e.is_team_lead ? (
                                    <div className="mcm-text-secondary" style={{ fontSize: '0.8rem' }}>lead</div>
                                  ) : null}
                                </div>
                              ) : '-'}
                            </td>
                            <td style={{ padding: '0.65rem' }}>{e.access_type}</td>
                            <td style={{ padding: '0.65rem' }}>
                              {statusText}
                              {voteDone ? (
                                <span className="mcm-text-success" style={{ marginLeft: 8, fontWeight: 700 }}>(complete)</span>
                              ) : null}
                            </td>
                            <td style={{ padding: '0.65rem' }}>
                              {e.access_type !== 'competitor'
                                ? '-'
                                : canViewScores
                                  ? formatScore(e)
                                  : 'Admin only'}
                            </td>
                            <td style={{ padding: '0.65rem' }}>
                              {canOpenSubmission(e) ? (
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="secondary"
                                  onClick={() => window.open(`/submission/${competitionId}?username=${encodeURIComponent(e.username)}`, '_blank')}
                                >
                                  View
                                </Button>
                              ) : (
                                <span className="mcm-text-muted">
                                  {e.access_type === 'competitor'
                                    ? (canViewAllSubmissions ? 'No submission' : 'Admin only')
                                    : '-'}
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="register-modal__actions">
                <Button type="button" variant="secondary" onClick={onClose}>
                  Close
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageCompetitionAccessListModal;
